import { useMemo } from "react";
import { Link } from "react-router-dom";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import {
  TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight,
  AlertTriangle, CheckCircle, Wallet, PoundSterling, Percent, Banknote
} from "lucide-react";
import { useApp, fmt, fmtShort } from "../../context/AppContext";

const RADIAN = Math.PI / 180;
const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
  const r = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + r * Math.cos(-midAngle * RADIAN);
  const y = cy + r * Math.sin(-midAngle * RADIAN);
  if (percent < 0.05) return null;
  return (
    <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={600}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 shadow-xl">
        <p className="text-slate-300 text-sm font-medium">{payload[0].name}</p>
        <p className="text-white font-bold">{fmt(payload[0].value)}</p>
        <p className="text-slate-500 text-xs">Ideal: {payload[0].payload.ideal}%</p>
      </div>
    );
  }
  return null;
};

function KpiCard({ title, value, icon: Icon, iconColor, valueColor, sub, badge, "data-testid": testId }) {
  return (
    <div data-testid={testId} className="bg-slate-900/40 backdrop-blur-sm border border-white/5 rounded-xl p-5 relative overflow-hidden group hover:border-white/10 transition-colors duration-200">
      <div className="flex items-start justify-between mb-3">
        <div className={`w-9 h-9 rounded-lg ${iconColor} flex items-center justify-center`}>
          <Icon className="w-4 h-4" />
        </div>
        {badge && (
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${badge.cls}`}>
            {badge.text}
          </span>
        )}
      </div>
      <p className="text-slate-500 text-xs mb-1">{title}</p>
      <p className={`font-outfit font-bold text-2xl tabular-nums tracking-tight ${valueColor}`}>{value}</p>
      {sub && <p className="text-slate-600 text-xs mt-1">{sub}</p>}
      <div className="absolute -bottom-4 -right-4 w-20 h-20 rounded-full opacity-5 blur-xl" style={{ background: iconColor.includes("emerald") ? "#10b981" : iconColor.includes("rose") ? "#f43f5e" : "#3b82f6" }} />
    </div>
  );
}

export default function Dashboard() {
  const { totalIncome, totalNeeds, totalWants, totalSavings, totalExpenses, leftover, savingsRate } = useApp();

  const needsPct = totalIncome > 0 ? (totalNeeds / totalIncome) * 100 : 0;
  const wantsPct = totalIncome > 0 ? (totalWants / totalIncome) * 100 : 0;
  const savingsPct = totalIncome > 0 ? (totalSavings / totalIncome) * 100 : 0;
  const leftoverPct = totalIncome > 0 ? Math.max(0, (leftover / totalIncome) * 100) : 0;
  const lifestyleCreep = needsPct > 50;
  const overWants = wantsPct > 30;

  const donutData = [
    { name: "Needs", value: totalNeeds, color: "#f43f5e", ideal: 50 },
    { name: "Wants", value: totalWants, color: "#f59e0b", ideal: 30 },
    { name: "Savings", value: totalSavings, color: "#10b981", ideal: 20 },
    ...(leftover > 0 ? [{ name: "Unallocated", value: leftover, color: "#475569", ideal: 0 }] : []),
  ].filter((d) => d.value > 0);

  const idealData = [
    { name: "Needs (Ideal)", value: totalIncome * 0.5, color: "#f43f5e40" },
    { name: "Wants (Ideal)", value: totalIncome * 0.3, color: "#f59e0b40" },
    { name: "Savings (Ideal)", value: totalIncome * 0.2, color: "#10b98140" },
  ];

  const topExpenses = useMemo(() => {
    const { monthData } = { monthData: { expenses: [] } };
    return [];
  }, []);

  return (
    <div className="space-y-6">
      {/* Warnings */}
      {lifestyleCreep && (
        <div data-testid="lifestyle-creep-banner" className="flex items-center gap-3 bg-amber-500/10 border border-amber-500/25 rounded-xl px-4 py-3">
          <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
          <div>
            <span className="text-amber-400 font-semibold text-sm">Lifestyle Creep Detected — </span>
            <span className="text-slate-400 text-sm">
              Your Needs spend is <span className="text-amber-400 font-medium">{needsPct.toFixed(0)}%</span> of income, above the recommended 50%. Review fixed costs.
            </span>
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          title="Total Income"
          value={fmt(totalIncome)}
          icon={PoundSterling}
          iconColor="bg-emerald-500/15 text-emerald-400"
          valueColor="text-emerald-400"
          sub="This month"
          data-testid="kpi-income"
        />
        <KpiCard
          title="Total Expenses"
          value={fmt(totalExpenses)}
          icon={ArrowDownRight}
          iconColor="bg-rose-500/15 text-rose-400"
          valueColor="text-rose-400"
          sub={`Needs ${fmt(totalNeeds)} + Wants ${fmt(totalWants)}`}
          data-testid="kpi-expenses"
        />
        <KpiCard
          title="Savings Rate"
          value={`${savingsRate}%`}
          icon={Percent}
          iconColor="bg-blue-500/15 text-blue-400"
          valueColor={savingsRate >= 20 ? "text-emerald-400" : savingsRate >= 10 ? "text-amber-400" : "text-rose-400"}
          sub={savingsRate >= 20 ? "On track" : "Below 20% target"}
          badge={savingsRate >= 20 ? { text: "On Track", cls: "bg-emerald-500/10 text-emerald-400" } : null}
          data-testid="kpi-savings-rate"
        />
        <KpiCard
          title="Monthly Leftover"
          value={fmt(leftover)}
          icon={leftover >= 0 ? TrendingUp : TrendingDown}
          iconColor={leftover >= 0 ? "bg-emerald-500/15 text-emerald-400" : "bg-rose-500/15 text-rose-400"}
          valueColor={leftover >= 0 ? "text-emerald-400" : "text-rose-400"}
          sub={leftover >= 0 ? "Cash flow positive" : "Over budget"}
          data-testid="kpi-leftover"
        />
      </div>

      {/* Chart Row */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* 50/30/20 Donut */}
        <div className="lg:col-span-3 bg-slate-900/40 border border-white/5 rounded-xl p-6" data-testid="donut-chart">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="font-outfit font-semibold text-white text-base">50 / 30 / 20 Breakdown</h2>
              <p className="text-slate-500 text-xs mt-0.5">Needs · Wants · Savings vs ideal split</p>
            </div>
            {lifestyleCreep && (
              <span className="flex items-center gap-1.5 text-xs font-semibold bg-amber-500/15 text-amber-400 border border-amber-500/25 rounded-full px-3 py-1">
                <AlertTriangle className="w-3 h-3" /> Lifestyle Creep
              </span>
            )}
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-6">
            <div className="relative flex-shrink-0" style={{ width: 192, height: 192 }}>
              <ResponsiveContainer width={192} height={192}>
                <PieChart>
                  <Pie data={donutData} cx="50%" cy="50%" innerRadius={58} outerRadius={84} paddingAngle={3} dataKey="value" labelLine={false} label={renderCustomLabel}>
                    {donutData.map((entry, i) => (<Cell key={i} fill={entry.color} />))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <p className="text-slate-500 text-[10px]">TOTAL SPEND</p>
                <p className="font-outfit font-bold text-white text-lg tabular-nums">{fmtShort(totalNeeds + totalWants)}</p>
              </div>
            </div>

            <div className="flex-1 space-y-3 w-full">
              {[
                { label: "Needs", actual: needsPct, ideal: 50, amount: totalNeeds, color: "bg-rose-500", textColor: "text-rose-400", over: needsPct > 50 },
                { label: "Wants", actual: wantsPct, ideal: 30, amount: totalWants, color: "bg-amber-500", textColor: "text-amber-400", over: wantsPct > 30 },
                { label: "Savings", actual: savingsPct, ideal: 20, amount: totalSavings, color: "bg-emerald-500", textColor: "text-emerald-400", over: false },
              ].map((row) => (
                <div key={row.label}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${row.color}`} />
                      <span className="text-slate-400 text-xs">{row.label}</span>
                      {row.over && <span className="text-[10px] text-amber-400 font-bold">OVER</span>}
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span className={`font-semibold tabular-nums ${row.textColor}`}>{row.actual.toFixed(0)}%</span>
                      <span className="text-slate-600">vs {row.ideal}% ideal</span>
                    </div>
                  </div>
                  <div className="w-full h-1.5 bg-slate-800 rounded-full overflow-hidden">
                    <div className={`h-full ${row.color} rounded-full transition-all duration-500`} style={{ width: `${Math.min(row.actual, 100)}%` }} />
                  </div>
                </div>
              ))}

              <div className="pt-2 border-t border-white/5">
                <div className="flex justify-between text-xs">
                  <span className="text-slate-500">Unallocated / Leftover</span>
                  <span className={`font-semibold tabular-nums ${leftover >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{fmt(leftover)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Breakdown list */}
        <div className="lg:col-span-2 bg-slate-900/40 border border-white/5 rounded-xl p-6">
          <h2 className="font-outfit font-semibold text-white text-base mb-4">Category Summary</h2>
          <div className="space-y-3">
            {[
              { label: "Needs (Essential)", amount: totalNeeds, pct: needsPct, color: "text-rose-400", bg: "bg-rose-500/10" },
              { label: "Wants (Discretionary)", amount: totalWants, pct: wantsPct, color: "text-amber-400", bg: "bg-amber-500/10" },
              { label: "Savings / Investments", amount: totalSavings, pct: savingsPct, color: "text-emerald-400", bg: "bg-emerald-500/10" },
            ].map((item) => (
              <div key={item.label} className={`flex items-center justify-between ${item.bg} rounded-lg px-3 py-2.5`}>
                <span className="text-slate-300 text-sm">{item.label}</span>
                <div className="text-right">
                  <p className={`font-outfit font-bold text-sm tabular-nums ${item.color}`}>{fmt(item.amount)}</p>
                  <p className="text-slate-600 text-[10px]">{item.pct.toFixed(1)}% of income</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 pt-4 border-t border-white/5">
            <div className="flex justify-between items-center">
              <span className="text-slate-400 text-sm">Total Outgoings</span>
              <span className="font-outfit font-bold text-white tabular-nums">{fmt(totalExpenses + totalSavings)}</span>
            </div>
            <div className="flex justify-between items-center mt-1.5">
              <span className="text-slate-500 text-xs">Effective Savings Rate</span>
              <span className={`font-semibold text-sm tabular-nums ${savingsRate >= 20 ? "text-emerald-400" : "text-amber-400"}`}>{savingsRate}%</span>
            </div>
          </div>

          <Link
            to="/dashboard/budget"
            className="mt-4 flex items-center justify-center gap-2 w-full bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 hover:text-white text-sm font-medium rounded-lg py-2.5 transition-colors duration-150"
          >
            <Wallet className="w-4 h-4" /> Manage Budget
          </Link>
        </div>
      </div>
    </div>
  );
}
